import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, Sparkles, ChevronDown, Globe, Smartphone, Lightbulb } from "lucide-react";
import { Layout } from "../components/Layout";
import { CategoryCard } from "../components/CategoryCard";
import { BusinessGrid } from "../components/BusinessGrid";
import { StartupCard } from "../components/StartupCard";
import { LogoBadge } from "../components/LogoBadge";
import { AboutModal } from "../components/AboutModal";
import { HomeHighlights, GoProBanner } from "../components/HomeHighlights";
import { ScannerPromo } from "../components/ScannerPromo";
import { CountryIdeasPromo } from "../components/CountryIdeasPromo";
import { FounderResourceLibrary } from "../components/FounderResourceLibrary";
import { fetchCategories, fetchBusinessesPage, fetchStartups, LOGO_URL } from "../lib/api";
import { useMarketplaceUnlock } from "../hooks/useMarketplaceUnlock";

export default function Home() {
  const navigate = useNavigate();
  const mp = useMarketplaceUnlock();
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [startups, setStartups] = useState([]);
  const [query, setQuery] = useState("");
  const [showAbout, setShowAbout] = useState(false);
  const [buildOpen, setBuildOpen] = useState(false);

  const buildOptions = [
    { icon: Globe, label: "Let's Build a Website", action: () => navigate("/apps") },
    { icon: Smartphone, label: "Let's Build an App", action: () => navigate("/apps") },
    { icon: Lightbulb, label: "Let's Build an Idea", action: () => window.dispatchEvent(new CustomEvent("lbs:open-blueprint", { detail: { mode: "have_idea" } })) },
  ];

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
    fetchBusinessesPage({ limit: 6, offset: 0 }).then((d) => setFeatured(d.items || [])).catch(() => {});
    fetchStartups().then((d) => setStartups((d.startups || []).slice(0, 4))).catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/business-ideas${query.trim() ? `?q=${encodeURIComponent(query.trim())}` : ""}`);
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-8">
        <div className="relative overflow-hidden rounded-[2rem] bg-[#0F0F0F] px-8 py-16 md:px-16 md:py-24">
          <div
            className="absolute inset-0 opacity-70"
            style={{
              background:
                "radial-gradient(circle at 78% 55%, rgba(212,175,55,0.28) 0%, rgba(212,175,55,0) 45%)",
            }}
          />
          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div className="animate-fade-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-[#D4AF37] mb-6">
                <Sparkles size={14} /> Founder Inspiration Platform
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white">
                Discover. <span className="text-[#D4AF37]">Inspire.</span> Build.
              </h1>
              <p className="mt-6 text-base md:text-lg text-[#A1A1AA] max-w-lg leading-relaxed">
                Explore professionally designed startup ideas that inspire entrepreneurs to
                create businesses uniquely their own.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <div className="relative">
                  <button
                    onClick={() => setBuildOpen((o) => !o)}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-8 py-4 font-semibold hover:bg-[#c5a030] transition-colors duration-300"
                    data-testid="hero-lets-build-btn"
                  >
                    Let's Build <ChevronDown size={18} className={`transition-transform duration-300 ${buildOpen ? "rotate-180" : ""}`} />
                  </button>
                  {buildOpen && (
                    <>
                      <div className="fixed inset-0 z-30" onClick={() => setBuildOpen(false)} />
                      <div className="absolute left-0 top-full mt-2 z-40 w-64 rounded-2xl bg-[#0F0F0F] border border-white/10 shadow-2xl p-2 animate-in fade-in slide-in-from-top-2 duration-200" data-testid="lets-build-menu">
                        {buildOptions.map((o) => (
                          <button
                            key={o.label}
                            onClick={() => { setBuildOpen(false); o.action(); }}
                            className="w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left text-white hover:bg-white/10 transition-colors"
                            data-testid={`build-option-${o.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                          >
                            <span className="w-8 h-8 rounded-lg bg-[#D4AF37] text-[#0F0F0F] flex items-center justify-center shrink-0"><o.icon size={16} /></span>
                            <span className="text-sm font-semibold">{o.label}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <button
                  onClick={() => navigate("/discover")}
                  className="inline-flex items-center justify-center bg-transparent text-white border border-white/25 rounded-full px-8 py-4 font-medium hover:bg-white/10 transition-colors duration-300"
                  data-testid="hero-lets-discover-btn"
                >
                  Let's Discover
                </button>
              </div>

              <button
                type="button"
                onClick={() => setShowAbout(true)}
                className="mt-8 inline-flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm px-5 py-4 text-left hover:bg-white/10 hover:border-white/20 transition-colors duration-300 group/card"
                data-testid="launch-startup-card"
              >
                <LogoBadge size="w-12 h-12" rounded="rounded-xl" />
                <div className="text-left">
                  <p className="font-display font-semibold text-white leading-tight flex items-center gap-1.5">
                    Launch Business Startup
                    <ArrowRight size={15} className="text-[#D4AF37] transition-transform duration-300 group-hover/card:translate-x-1" />
                  </p>
                  <p className="text-xs text-[#A1A1AA] mt-0.5">
                    Learn what LaunchBS is all about.
                  </p>
                </div>
              </button>
            </div>
            <div className="hidden md:flex justify-center">
              <img
                src={LOGO_URL}
                alt="LaunchBS rocket"
                className="w-72 lg:w-[26rem] object-contain animate-float drop-shadow-[0_30px_80px_rgba(212,175,55,0.45)]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* AI Opportunity Scanner promo */}
      <ScannerPromo />

      {/* AI Country Ideas promo */}
      <CountryIdeasPromo />

      {/* Feature highlights row (Start Your Journey · AI Tools · Learn) */}
      <HomeHighlights />

      {/* Founder Resource Library */}
      <FounderResourceLibrary />

      {/* Ready-to-Build Startups */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37]/12 px-3 py-1 text-[11px] font-semibold text-[#B8901F] mb-2">
              The world's first Ready-to-Build Startup marketplace
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0F0F0F]">
              Ready-to-Build Startups
            </h2>
            <p className="text-[#687280] mt-2">Professionally architected startup projects, professionally built and ready to transfer.</p>
          </div>
          <button
            onClick={() => mp.guard(() => navigate("/apps"))}
            className="text-sm font-medium text-[#D4AF37] hover:underline shrink-0"
            data-testid="view-all-startups-btn"
          >
            Browse Startups
          </button>
        </div>
        {startups.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {startups.map((s, i) => <StartupCard key={s.id} startup={s} index={i} />)}
          </div>
        ) : (
          <div className="bg-[#F7F7F7] rounded-2xl border border-dashed border-gray-200 py-16 text-center text-[#687280]">Startups are loading…</div>
        )}
      </section>

      {/* Go Pro banner */}
      <GoProBanner />

      {/* Search */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-20">
        <div className="bg-[#F7F7F7] rounded-[2rem] px-8 py-14 text-center">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-[#0F0F0F] mb-2">
            Search Business Ideas
          </h2>
          <p className="text-[#687280] mb-8">Find ideas by name, category, or industry.</p>
          <form onSubmit={submitSearch} className="max-w-2xl mx-auto">
            <div className="flex items-center gap-3 bg-white rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-gray-100 px-6 py-4">
              <Search size={20} className="text-[#687280] shrink-0" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search business ideas..."
                className="w-full text-base outline-none bg-transparent placeholder-[#9CA3AF] text-[#0F0F0F]"
                data-testid="home-search-input"
              />
              <button
                type="submit"
                className="shrink-0 bg-[#0F0F0F] text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-[#D4AF37] hover:text-[#0F0F0F] transition-colors"
                data-testid="home-search-btn"
              >
                Search
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Featured Businesses */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-20">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0F0F0F]">
            Ideas Worth Building
          </h2>
          <button
            onClick={() => navigate("/business-ideas")}
            className="text-sm font-medium text-[#D4AF37] hover:underline shrink-0"
            data-testid="view-all-ideas-btn"
          >
            View All
          </button>
        </div>
        <BusinessGrid businesses={featured} />
        {featured.length === 0 && (
          <div className="bg-[#F7F7F7] rounded-2xl border border-dashed border-gray-200 py-16 px-8 text-center" data-testid="home-ideas-empty">
            <p className="font-display text-xl font-semibold text-[#0F0F0F]">Fresh ideas are on the way</p>
            <p className="text-[#687280] mt-2">We're curating premium business ideas across 40 categories. Explore the categories in the meantime.</p>
            <button
              onClick={() => navigate("/categories")}
              className="mt-5 inline-flex items-center gap-2 bg-[#0F0F0F] text-white rounded-full px-6 py-3 text-sm font-semibold hover:bg-[#1c1c1c] transition-colors"
              data-testid="home-explore-categories-btn"
            >
              Explore Categories <ArrowRight size={16} />
            </button>
          </div>
        )}
      </section>

      <AboutModal open={showAbout} onClose={() => setShowAbout(false)} />
    </Layout>
  );
}
